/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode } from 'react';
import { stripTrackingParams } from '../urlUtils';

// Known web event types from GQL (eventType is the specific type, not "website")
const WEB_EVENT_TYPES = new Set([
  'website', 'property_view', 'property_saved', 'lead_capture', 'search', 'page_view', 'custom',
]);

function tryParseJSON(val: unknown): Record<string, any> {
  if (!val) return {};
  if (typeof val === 'object') return val as Record<string, any>;
  try { return JSON.parse(val as string); } catch { return {}; }
}

export interface ActivityDescriptionInput {
  eventType: string;
  eventSubtype?: string;
  channel?: string;
  summary?: string;
  outcome?: string;
  durationSeconds?: number;
  metadata?: string | Record<string, any>;
}

/**
 * Shared description builder used by both RecentActivities and Contact Activity Feed.
 * Returns a ReactNode — either plain string or clickable links for web events.
 */
export function buildActivityDescription(item: ActivityDescriptionInput): ReactNode {
  const type = item.eventType?.toLowerCase() ?? '';

  // Parse metadata once
  const meta: Record<string, any> = typeof item.metadata === 'string'
    ? (() => { try { return JSON.parse(item.metadata as string); } catch { return {}; } })()
    : (item.metadata ?? {});

  // ── Calls ──────────────────────────────────────────────────────────
  // Duration is rendered inline with the title by the consuming component.
  // Description shows the call summary.
  if (type === 'call' || type.includes('call')) {
    return item.summary || '';
  }

  // ── Emails ─────────────────────────────────────────────────────────
  if (type === 'email') {
    const subject = meta.subject || '';
    if (subject) return subject;
    return item.summary || '';
  }

  // ── Website ────────────────────────────────────────────────────────
  const isWebType = WEB_EVENT_TYPES.has(type) || item.channel === 'web';
  if (isWebType) {
    const props = tryParseJSON(meta.properties);
    const pageUrl = meta.page_url || meta.pageUrl || '';
    const cleanedUrl = pageUrl ? stripTrackingParams(pageUrl) : '';

    const isPropertyEvent = ['property_view', 'property_saved', 'lead_capture'].includes(type);
    const isSearchEvent = type === 'search';

    if (isPropertyEvent && props.address && props.city) {
      const propertyInfo = `${props.address}, ${props.city}${props.state ? `, ${props.state}` : ''}${props.zipCode ? ` ${props.zipCode}` : ''}`;
      return (
        <a href={cleanedUrl} target="_blank" rel="noopener noreferrer" className="underline block truncate max-w-[400px]" onClick={(e) => e.stopPropagation()} title={propertyInfo}>
          {propertyInfo}
        </a>
      );
    }

    if (isSearchEvent) {
      let searchLabel = '';
      if (props.searchQuery?.trim()) {
        searchLabel = props.searchQuery;
      } else if (props.searchFilters?.filters) {
        const f = props.searchFilters.filters;
        const loc = props.searchFilters.location;
        const p: string[] = [];
        if (f.min_price) p.push(`$${f.min_price.toLocaleString()}+`);
        if (f.property_sub_type) p.push(f.property_sub_type);
        if (f.standard_status) p.push(f.standard_status);
        if (loc?.city) p.push(loc.city);
        if (loc?.state) p.push(loc.state);
        searchLabel = p.join(', ');
      }
      if (searchLabel) {
        return (
          <a href={cleanedUrl} target="_blank" rel="noopener noreferrer" className="underline block truncate max-w-[400px]" onClick={(e) => e.stopPropagation()} title={searchLabel}>
            Search: {searchLabel}
          </a>
        );
      }
    }

    if (cleanedUrl) {
      return (
        <a href={cleanedUrl} target="_blank" rel="noopener noreferrer" className="underline block truncate max-w-[400px]" onClick={(e) => e.stopPropagation()} title={cleanedUrl}>
          {cleanedUrl}
        </a>
      );
    }
  }

  return item.summary || '';
}
